/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { ApiBaseUrl } from "configs";
import { authRefreshTokenHandler } from "services";
import store from "stores";
import { systemSlice } from "stores/System";
import { loadAccessToken, loadSessionStorage } from "utils";

export type BaseAxiosRequestConfig = AxiosRequestConfig & {
  requireAuthentication?: boolean;
};

declare module "axios" {
  export interface AxiosInstance {
    request<T = any, R = AxiosResponse<T>>(
      config: BaseAxiosRequestConfig
    ): Promise<R>;
    get<T = any, R = AxiosResponse<T>>(
      url: string,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    delete<T = any, R = AxiosResponse<T>>(
      url: string,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    head<T = any, R = AxiosResponse<T>>(
      url: string,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    options<T = any, R = AxiosResponse<T>>(
      url: string,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    post<T = any, D = any, R = AxiosResponse<T>>(
      url: string,
      data?: D,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    put<T = any, D = any, R = AxiosResponse<T>>(
      url: string,
      data?: D,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
    patch<T = any, D = any, R = AxiosResponse<T>>(
      url: string,
      data?: D,
      config?: BaseAxiosRequestConfig
    ): Promise<R>;
  }
}

type FailedQueueTypes = {
  reject: (error: Error) => void;
  resolve: (value: unknown) => void;
};

export enum HTTPRequestMethods {
  REQUEST = "request",
  GET = "get",
  POST = "post",
  PUT = "put",
  PATCH = "patch",
  DELETE = "delete",
  OPTIONS = "options",
}

class RestAPIClient {
  private instance?: AxiosInstance;

  private isTokenRefreshing = false;

  private failedQueueByInvalidatedToken: Array<FailedQueueTypes> = [];

  constructor(baseConfig: BaseAxiosRequestConfig) {
    if (!baseConfig) throw new Error("Base config should not be empty!!!");

    this.instance = axios.create(baseConfig);

    this.instance.interceptors.request.use(
      (config) => {
        if (!config?.headers) {
          throw new Error(
            `Expected 'config' and 'config.headers' not to be undefined`
          );
        }

        if (
          (config as unknown as BaseAxiosRequestConfig).requireAuthentication
        ) {
          delete (config as unknown as BaseAxiosRequestConfig)
            .requireAuthentication;

          const accessToken = loadAccessToken();
          config.headers.Authorization = `Bearer ${accessToken ?? ""}`;

          const organization = loadSessionStorage("Organization");
          config.headers["x-organization-id"] = organization?.id;
          config.headers["x-tenant-code"] = organization?.tenantCode;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        const originalRequest =
          error.config as unknown as InternalAxiosRequestConfig;

        const errorStatus = error.response?.status;
        const errorMessage = (
          error.response?.data as {
            message: Record<string, unknown>;
          }
        )?.message;

        if (errorStatus) {
          if (errorStatus === 403 && !!errorMessage.invalid_token) {
            // NOTE: when token is updating, return API connection queue
            if (this.isTokenRefreshing) {
              return new Promise((resolve, reject) => {
                this.setFailedQueueByInvalidatedToken({ resolve, reject });
              })
                .then(() => {
                  return this.call({
                    method: HTTPRequestMethods.REQUEST,
                    config: {
                      ...originalRequest,
                      requireAuthentication: true,
                    },
                  });
                })
                .catch((err) => {
                  return Promise.reject(err);
                });
            }

            this.setTokenRefreshing(true);

            return new Promise((resolve, reject) => {
              authRefreshTokenHandler()
                .then((res) => {
                  this.processQueue(null, res.accessToken);
                  resolve(
                    this.call({
                      method: HTTPRequestMethods.REQUEST,
                      config: {
                        ...originalRequest,
                        requireAuthentication: true,
                      },
                    })
                  );
                })
                .catch((err: AxiosError) => {
                  this.processQueue(err, null);
                  // NOTE: Handle expired session case
                  reject(err);
                })
                .finally(() => {
                  this.setTokenRefreshing(false);
                });
            });
          }

          if (errorStatus > 500) {
            store.dispatch(systemSlice.actions.showInternalServerError());
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue = (error: Error | null, token: string | null = null) => {
    this.failedQueueByInvalidatedToken.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.clearFailedQueueByInvalidatedToken();
  };

  private setTokenRefreshing = (value: boolean) => {
    this.isTokenRefreshing = value;
  };

  private setFailedQueueByInvalidatedToken = (value: FailedQueueTypes) => {
    this.failedQueueByInvalidatedToken.push(value);
  };

  private clearFailedQueueByInvalidatedToken = () => {
    this.failedQueueByInvalidatedToken = [];
  };

  originalCall = <
    T = any,
    D = any,
    E = any,
    R = AxiosResponse<RestAPIResponse<T, E>>
  >(params: {
    method: HTTPRequestMethods;
    url?: string;
    data?: D;
    config?: BaseAxiosRequestConfig;
  }): Promise<R> => {
    if (!this.instance)
      throw new Error("HTTP instance has not been supplied!!!");
    const { method, url, data, config } = params;
    switch (method) {
      case HTTPRequestMethods.REQUEST: {
        if (!config) throw new Error("Config has not been supplied!!!");
        return this.instance.request(config);
      }
      case HTTPRequestMethods.GET:
      case HTTPRequestMethods.DELETE:
      case HTTPRequestMethods.OPTIONS: {
        if (!url) throw new Error("URL has not been supplied!!!");
        return this.instance[method]?.(url, config);
      }
      case HTTPRequestMethods.POST:
      case HTTPRequestMethods.PUT:
      case HTTPRequestMethods.PATCH: {
        if (!url) throw new Error("URL has not been supplied!!!");
        return this.instance[method]?.(url, data, config);
      }
      default:
        throw new Error(
          `The ${(method as string).toUpperCase()} method does not support!!!`
        );
    }
  };

  call = async <T = any, D = any, E = any, R = RestAPIResponse<T, E>>(params: {
    method: HTTPRequestMethods;
    url?: string;
    data?: D;
    config?: BaseAxiosRequestConfig;
  }): Promise<R> => {
    if (!this.instance)
      throw new Error("HTTP instance has not been supplied!!!");
    const { method, url, data, config } = params;
    switch (method) {
      case HTTPRequestMethods.REQUEST: {
        if (!config) throw new Error("Config has not been supplied!!!");
        const res: AxiosResponse<R> = await this.instance.request(config);
        return res?.data;
      }
      case HTTPRequestMethods.GET:
      case HTTPRequestMethods.DELETE:
      case HTTPRequestMethods.OPTIONS: {
        if (!url) throw new Error("URL has not been supplied!!!");
        const res: AxiosResponse<R> = await this.instance[method]?.(
          url,
          config
        );
        return res?.data;
      }
      case HTTPRequestMethods.POST:
      case HTTPRequestMethods.PUT:
      case HTTPRequestMethods.PATCH: {
        if (!url) throw new Error("URL has not been supplied!!!");
        const res: AxiosResponse<R> = await this.instance[method]?.(
          url,
          data,
          config
        );
        return res?.data;
      }
      default:
        throw new Error(
          `The ${(method as string).toUpperCase()} method does not support!!!`
        );
    }
  };
}

export default new RestAPIClient({
  baseURL: ApiBaseUrl,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});
